import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type {
  TableSettingColumn,
  tableColumnValidation,
  exportTableColumnValidation,
} from 'types/tableSettingsTypes';

export interface DynamicModuleField {
  name: string;
  type: string;
  required?: boolean;
  is_array?: boolean;
  ref_model?: string;
  unique?: boolean;
  index?: boolean;
  is_slug_source?: boolean;
  media_type?: string;
  enum?: string[];
  ref_label_key?: string;
}

export interface DynamicModuleConfig {
  _id: string;
  module_name: string;
  schema_name: string;
  status: boolean;
  allowed_roles: string[];
  public_methods: string[];
  search_fields: string[];
  display_fields: string[];
  allowed_operations: string[];
  fields: DynamicModuleField[];
  created_at: string;
  updated_at: string;
}

export interface ModuleState {
  filterData: Record<string, any>;
  reloadTableData: boolean;
  tableColumnList: any[];
  exportTableColumnList: any[];
  defaultColumnList: any[];
}

interface AdminDynamicModuleState {
  modules: DynamicModuleConfig[];
  activeModule: DynamicModuleConfig | null;
  moduleStates: Record<string, ModuleState>;
  isLoading: boolean;
  tableColumnValidation: tableColumnValidation;
  exportTableColumnValidation: exportTableColumnValidation;
}

const initialState: AdminDynamicModuleState = {
  modules: [],
  activeModule: null,
  moduleStates: {},
  isLoading: false,
  tableColumnValidation: {
    minListColumn: 1,
    maxListColumn: 10,
    minFilterColumn: 0,
    maxFilterColumn: 10,
  },
  exportTableColumnValidation: {
    minListColumn: 1,
  },
};

const adminDynamicModuleSlice = createSlice({
  name: 'adminDynamicModule',
  initialState,
  reducers: {
    setModules(state, action: PayloadAction<DynamicModuleConfig[]>) {
      state.modules = action.payload;
    },
    setActiveModule(state, action: PayloadAction<DynamicModuleConfig | null>) {
      state.activeModule = action.payload;
      if (action.payload) {
        const schemaName = action.payload.schema_name;
        // Initialize module state if not exists
        if (!state.moduleStates[schemaName]) {
          // Generate defaultColumnList based on display_fields and fields
          const columns: any[] = action.payload.display_fields.map((fieldName, index) => {
            const field = action.payload?.fields.find((f) => f.name === fieldName);
            return {
              name: fieldName,
              label_key: fieldName,
              allow_list: true,
              allow_filter: action.payload?.search_fields.includes(fieldName),
              show_list_checkbox: true,
              show_filter_checkbox: action.payload?.search_fields.includes(fieldName),
              order: index + 1,
              type: field?.type,
              media_type: field?.media_type,
              enum: field?.enum,
              ref_label_key: field?.ref_label_key,
            };
          });

          // Add status and created_at if not present
          if (
            !columns.find((c) => c.name === 'status') &&
            (action.payload.allowed_operations.includes('status') ||
              action.payload.fields.find((f) => f.name === 'status'))
          ) {
            columns.push({
              name: 'status',
              label_key: 'status',
              allow_list: true,
              allow_filter: true,
              show_list_checkbox: true,
              show_filter_checkbox: true,
              order: columns.length + 1,
              type: 'select',
              filter_placeholder: 'status_all',
              selectKey: 'status',
              option_value_key: 'id',
              option_label_key: 'name',
              multi: false,
            });
          }

          state.moduleStates[schemaName] = {
            filterData: {},
            reloadTableData: false,
            defaultColumnList: columns,
            tableColumnList: JSON.parse(JSON.stringify(columns)),
            exportTableColumnList: JSON.parse(JSON.stringify(columns)),
          };
        }
      }
    },
    setFilterData(state, action: PayloadAction<Record<string, any>>) {
      if (state.activeModule) {
        const schemaName = state.activeModule.schema_name;
        state.moduleStates[schemaName].filterData = {
          ...state.moduleStates[schemaName].filterData,
          ...action.payload,
        };
      }
    },
    resetFilterData(state) {
      if (state.activeModule) {
        const schemaName = state.activeModule.schema_name;
        state.moduleStates[schemaName].filterData = {};
      }
    },
    setReloadTableData(state, action: PayloadAction<boolean>) {
      if (state.activeModule) {
        const schemaName = state.activeModule.schema_name;
        state.moduleStates[schemaName].reloadTableData = action.payload;
      }
    },
    setDynamicTableColumnList(state, action: PayloadAction<TableSettingColumn[]>) {
      if (state.activeModule) {
        const schemaName = state.activeModule.schema_name;
        state.moduleStates[schemaName].tableColumnList = action.payload;
      }
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
  },
});

export const {
  setModules,
  setActiveModule,
  setFilterData,
  resetFilterData,
  setReloadTableData,
  setDynamicTableColumnList,
  setLoading,
} = adminDynamicModuleSlice.actions;

export default adminDynamicModuleSlice.reducer;
